import _ from 'lodash';
import PropTypes from 'prop-types';
import { Fragment, memo, useCallback, useMemo } from 'react';
import NumberFormat from 'react-number-format';
import { Tooltip, withStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RiInformationLine } from 'react-icons/ri';
import { SearchStatus } from '../../../../constants';
import withSpinner from '../../../../HOCs/withSpinner';
import currencySymbol from '../../../../helpers/currencySymbol';
import parseRentalCarCharges from '../parseRentalCarCharges';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[3],
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
}))(Tooltip);

const CarDetails = ({
  itinerary,
  getCarRateRulesStatus,
  vendorMessages,
  doShowMarkupRate,
  carRateRules,
}) => {
  const { travelAgency } = useSelector((state) => state.user);
  const markupAmount =
    itinerary.priceInfo.markupPriceAllDays > 0
      ? +(itinerary.priceInfo.markupPriceAllDays - itinerary.priceInfo.priceAllDays).toFixed(2)
      : 0;
  const { baseRate, approxTotalRate, taxesAndFees, otherCharges, doShowTaxesAndFees } =
    parseRentalCarCharges(itinerary.priceInfo && itinerary.priceInfo.vehicleCharges);

  const serviceFeesElement =
    doShowMarkupRate && itinerary.priceInfo && markupAmount > 0 ? (
      <div className="flex">
        <b className="flex items-center">
          Service fee
          {travelAgency &&
            travelAgency.settings &&
            travelAgency.settings.taxesAndFeesToolTipText && (
              <LightTooltip
                title={travelAgency.settings.taxesAndFeesToolTipText}
                placement="bottom"
                arrow
                className="ml-1"
              >
                <div>
                  <RiInformationLine size={18} className="cursor-pointer" />
                </div>
              </LightTooltip>
            )}
          :
        </b>
        <NumberFormat
          value={markupAmount}
          prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
          displayType="text"
          thousandSeparator
          fixedDecimalScale="true"
          decimalScale="2"
          className="ml-1"
        />
      </div>
    ) : (
      <></>
    );

  const displayCharge = useCallback(
    (vehicleCharge) => (
      <div key={vehicleCharge.chargeType + vehicleCharge.amount}>
        <b>{vehicleCharge.chargeType}:</b>{' '}
        {vehicleCharge.markupAmount === 0 && vehicleCharge.amount === 0 ? (
          <span>N/A</span>
        ) : (
          <NumberFormat
            value={
              doShowMarkupRate && vehicleCharge.markupAmount > 0
                ? vehicleCharge.markupAmount
                : vehicleCharge.amount
            }
            prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
            displayType="text"
            thousandSeparator
            fixedDecimalScale="true"
            decimalScale="2"
          />
        )}
      </div>
    ),
    [doShowMarkupRate, itinerary.priceInfo.currencyCode],
  );

  const unit = useMemo(() => (carRateRules?.mileageUOM === 'KM' ? 'km' : 'mile'), [carRateRules]);
  const hasRateRulesBreakdown = useMemo(
    () =>
      Boolean(
        carRateRules &&
        (carRateRules.vehicleCharges?.length > 0 ||
          (carRateRules.fees && carRateRules.fees.length > 0)),
      ),
    [carRateRules],
  );

  return (
    <div className="col-span-6">
      {/* ── Vehicle Charges ── */}
      {!hasRateRulesBreakdown && getCarRateRulesStatus !== SearchStatus.SEARCHING && (
        <div className="mt-5">
          <b>Vehicle Charges:</b> <br />
          {doShowTaxesAndFees ? (
            <>
              {displayCharge(baseRate)}
              {displayCharge({
                chargeType: 'Taxes and Fees',
                amount: taxesAndFees,
                markupAmount: taxesAndFees,
              })}
              {serviceFeesElement}
              <hr className="my-1 w-64" />
              {displayCharge(approxTotalRate)}
            </>
          ) : (
            <></>
          )}
          {doShowTaxesAndFees && otherCharges && otherCharges.length ? (
            <div className="mt-5">
              <div className="font-bold">Other Charges:</div>
              {otherCharges.map((vehicleCharge) => displayCharge(vehicleCharge))}
            </div>
          ) : (
            <></>
          )}
          {!doShowTaxesAndFees &&
            itinerary.priceInfo.vehicleCharges &&
            itinerary.priceInfo.vehicleCharges.map((vehicleCharge, index) => (
              <Fragment key={vehicleCharge.chargeType + vehicleCharge.amount}>
                {index === itinerary.priceInfo.vehicleCharges.length - 1 && serviceFeesElement}
                {displayCharge(vehicleCharge)}
              </Fragment>
            ))}
        </div>
      )}

      {/* ── Rate Rules Details ── */}
      {carRateRules && (
        <div className="mt-5 text-sm">
          {/* Header */}
          <div className="text-gray-500 mb-2">
            All total prices are returned Direct Connect from car associate.
          </div>
          <hr className="mb-3" />

          {/* Rate Plan Table */}
          {(carRateRules.vehicleCharges?.length > 0 ||
            (carRateRules.fees && carRateRules.fees.length > 0)) && (
            <div className="mt-3 max-w-none lg:max-w-85">
              <b>Rate Plan in {itinerary.priceInfo.currencyCode}:</b>
              <table className="w-full mt-3 border-collapse">
                <thead>
                  <tr className="border-t border-gray-300 text-left text-gray-500">
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 font-medium">Rate</th>
                    <th className="py-2 pr-4 font-medium">Distance Limit</th>
                    <th className="py-2 pr-4 font-medium">Apply</th>
                    <th className="py-2 pr-4 font-medium">Equals</th>
                    <th className="py-2 pr-4 font-medium">Mileage Allowance</th>
                  </tr>
                </thead>
                <tbody>
                  {carRateRules.vehicleCharges &&
                    carRateRules.vehicleCharges.length > 0 &&
                    carRateRules.vehicleCharges.map((vehicleCharge) => (
                      <tr
                        className="border-t border-gray-100"
                        key={vehicleCharge.type + vehicleCharge.rate}
                      >
                        <td className="py-2 pr-4">{vehicleCharge.type}</td>
                        <td className="py-2 pr-4">
                          <NumberFormat
                            value={vehicleCharge.rate}
                            prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                            displayType="text"
                            thousandSeparator
                            fixedDecimalScale
                            decimalScale={2}
                          />
                        </td>
                        <td className="py-2 pr-4">
                          {vehicleCharge.mileageAllowance === 'UNL'
                            ? 'Unlimited'
                            : vehicleCharge.mileageAllowance > 0
                              ? `${vehicleCharge.mileageAllowance} ${unit}s`
                              : ''}
                        </td>
                        <td className="py-2 pr-4">{vehicleCharge.numOccurrences ?? '—'}</td>
                        <td className="py-2 pr-4">
                          {vehicleCharge.subTotal ? (
                            <NumberFormat
                              value={vehicleCharge.subTotal}
                              prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                              displayType="text"
                              thousandSeparator
                              fixedDecimalScale
                              decimalScale={2}
                            />
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 pr-4">
                          {vehicleCharge.totalMileageAllowance === 'UNL'
                            ? 'Unlimited'
                            : vehicleCharge.totalMileageAllowance > 0
                              ? `${vehicleCharge.totalMileageAllowance} ${unit}s`
                              : ''}
                        </td>
                      </tr>
                    ))}

                  {carRateRules.subTotal > 0 && (
                    <tr className="border-t border-gray-400 font-semibold">
                      <td className="py-2 pr-4">Subtotal</td>
                      <td className="py-2 pr-4"></td>
                      <td className="py-2 pr-4"></td>
                      <td className="py-2 pr-4"></td>
                      <td className="py-2 pr-4">
                        <NumberFormat
                          value={carRateRules.subTotal}
                          prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                          displayType="text"
                          thousandSeparator
                          fixedDecimalScale
                          decimalScale={2}
                        />
                      </td>
                      <td className="py-2 pr-4">
                        {carRateRules.extraMileageCharge > 0 ? (
                          <span>
                            {carRateRules.mileageAllowance} {unit}s included
                            <br />({currencySymbol(itinerary.priceInfo.currencyCode)}
                            {Number(carRateRules.extraMileageCharge).toFixed(2)} for every
                            additional {unit})
                          </span>
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  )}
                  {/* Additional Charges (Drop-off, Special Equipment) */}
                  {((carRateRules.dropOffCharge && carRateRules.dropOffCharge > 0) ||
                    (carRateRules.specialEquipTotalCharge &&
                      carRateRules.specialEquipTotalCharge > 0)) && (
                    <>
                      <tr>
                        <td colSpan={6} className="pt-3">
                          <b>Additional Charges:</b>
                        </td>
                      </tr>
                      {carRateRules.dropOffCharge && carRateRules.dropOffCharge > 0 && (
                        <tr className="border-t border-gray-100 mr-4">
                          <td className="py-2 pr-4" colSpan={4}>
                            Drop-off Charge
                          </td>
                          <td className="py-2 text-left whitespace-nowrap" colSpan={2}>
                            <NumberFormat
                              value={carRateRules.dropOffCharge}
                              prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                              displayType="text"
                              thousandSeparator
                              fixedDecimalScale
                              decimalScale={2}
                            />
                          </td>
                        </tr>
                      )}
                      {carRateRules.specialEquipTotalCharge &&
                        carRateRules.specialEquipTotalCharge > 0 && (
                          <tr className="border-t border-gray-100 mr-4">
                            <td className="py-2 pr-4" colSpan={4}>
                              Special Equipment Charge
                            </td>
                            <td className="py-2 text-left whitespace-nowrap" colSpan={2}>
                              <NumberFormat
                                value={carRateRules.specialEquipTotalCharge}
                                prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                                displayType="text"
                                thousandSeparator
                                fixedDecimalScale
                                decimalScale={2}
                              />
                            </td>
                          </tr>
                        )}
                    </>
                  )}
                  {/* Fees Table */}
                  {carRateRules.fees && carRateRules.fees.length > 0 && (
                    <>
                      <tr>
                        <td colSpan={6} className="pt-3">
                          <b>Mandatory Fees and Taxes:</b>
                        </td>
                      </tr>
                      {carRateRules.fees.map((fee) => (
                        <tr key={fee.description} className="border-t border-gray-100 mr-4">
                          <td className="py-2 pr-4" colSpan={4}>
                            {fee.description}
                          </td>
                          <td className="py-2 text-left whitespace-nowrap" colSpan={2}>
                            <NumberFormat
                              value={fee.amount}
                              prefix={currencySymbol(fee.currencyCode)}
                              displayType="text"
                              thousandSeparator
                              fixedDecimalScale
                              decimalScale={2}
                            />
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                  <tr className="border-t border-b border-gray-400 mr-4 font-bold">
                    <td className="py-2 pr-4" colSpan={4}>
                      Approximate Rental Cost
                    </td>
                    <td className="py-2 text-left whitespace-nowrap">
                      <NumberFormat
                        value={carRateRules.totalCharge}
                        prefix={currencySymbol(itinerary.priceInfo.currencyCode)}
                        displayType="text"
                        thousandSeparator
                        fixedDecimalScale
                        decimalScale={2}
                      />
                    </td>
                    <td className="py-2 text-left whitespace-nowrap">
                      {+approxTotalRate.mileageAllowance > 0 ||
                      +approxTotalRate.extraMileageCharge > 0 ? (
                        <span>
                          {approxTotalRate.mileageAllowance} {unit}s included
                          <br />({currencySymbol(itinerary.priceInfo.currencyCode)}
                          {parseFloat(approxTotalRate.extraMileageCharge).toFixed(2)} for every
                          additional {unit})
                        </span>
                      ) : (
                        ''
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Booking Rules */}
          {(carRateRules.guaranteeNumDays ||
            carRateRules.advanceBookingMinDays ||
            carRateRules.advanceBookingMaxDays ||
            carRateRules.advanceBookingMaxPeriod ||
            carRateRules.numDays > 0 ||
            (carRateRules.pickupLocationOperationTimes &&
              carRateRules.pickupLocationOperationTimes.length > 0)) && (
            <div className="mt-4">
              <b>Booking Rules:</b>
              <table className="mt-1 border-collapse">
                <tbody>
                  {carRateRules.pickupLocationOperationTimes &&
                    carRateRules.pickupLocationOperationTimes.length > 0 && (
                      <tr className="border-t border-gray-100">
                        <td className="py-2 pr-4">Pickup Location Hours</td>
                        <td className="py-2">
                          {carRateRules.pickupLocationOperationTimes
                            .filter((t) => t && (t.start || t.end))
                            .map((t) => `${t.start || '—'}–${t.end || '—'}`)
                            .join(', ')}
                        </td>
                      </tr>
                    )}
                  {carRateRules.numDays > 0 && (
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">Length of Rental</td>
                      <td className="py-2">
                        {carRateRules.numDays} day(s)
                        {carRateRules.numHours > 0 ? `, ${carRateRules.numHours} hour(s)` : ''}
                      </td>
                    </tr>
                  )}
                  {carRateRules.advanceBookingMinDays && (
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">Minimum Rental</td>
                      <td className="py-2">{carRateRules.advanceBookingMinDays} day(s)</td>
                    </tr>
                  )}
                  {carRateRules.guaranteeNumDays && (
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4"> Rate guaranteed for </td>
                      <td className="py-2">{carRateRules.guaranteeNumDays} days</td>
                    </tr>
                  )}
                  {carRateRules.advanceBookingMaxDays && (
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">Maximum Rental</td>
                      <td className="py-2">{carRateRules.advanceBookingMaxDays} day(s)</td>
                    </tr>
                  )}
                  {carRateRules.advanceBookingMaxPeriod && (
                    <tr className="border-t border-gray-100">
                      <td className="py-2 pr-4">Maximum Period</td>
                      <td className="py-2">{carRateRules.advanceBookingMaxPeriod} days</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Vendor Messages ── */}
      {withSpinner(
        () =>
          vendorMessages &&
          Object.keys(vendorMessages).length > 0 && (
            <div className="mt-5">
              {_.orderBy(Object.keys(vendorMessages), [], ['desc']).map((key) => (
                <div className="whitespace-normal mt-2" key={key + vendorMessages[key]}>
                  <b>{key} </b>
                  <br />
                  <pre className="font-body ml-5 whitespace-pre-wrap">{vendorMessages[key]}</pre>
                </div>
              ))}
            </div>
          ),
        getCarRateRulesStatus === SearchStatus.SEARCHING,
        'Retrieving rate rules...',
      )}
    </div>
  );
};

CarDetails.propTypes = {
  itinerary: PropTypes.objectOf(PropTypes.objectOf),
  getCarRateRulesStatus: PropTypes.number,
  vendorMessages: PropTypes.objectOf(PropTypes.objectOf),
  doShowMarkupRate: PropTypes.bool,
  carRateRules: PropTypes.shape({
    mileageAllowance: PropTypes.string,
    mileageUOM: PropTypes.string,
    extraMileageCharge: PropTypes.number,
    baseRate: PropTypes.number,
    subTotal: PropTypes.number,
    totalCharge: PropTypes.number,
    baseRatePeriodLabel: PropTypes.string,
    extraDayRate: PropTypes.number,
    extraDayMileageAllowance: PropTypes.string,
    extraDayNumOccurrences: PropTypes.number,
    extraDayTimesOccurrenceAmount: PropTypes.number,
    extraDayMileageTimesOccurrence: PropTypes.string,
    extraHourRate: PropTypes.number,
    extraHourMileageAllowance: PropTypes.string,
    extraHourNumOccurrences: PropTypes.number,
    extraHourTimesOccurrenceAmount: PropTypes.number,
    extraHourMileageTimesOccurrence: PropTypes.string,
    dropOffCharge: PropTypes.number,
    specialEquipTotalCharge: PropTypes.number,
    fees: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        amount: PropTypes.number,
        currencyCode: PropTypes.string,
      }),
    ),
    guaranteeNumDays: PropTypes.number,
    advanceBookingMinDays: PropTypes.number,
    numDays: PropTypes.number,
    numHours: PropTypes.number,
    baseRateNumOccurrences: PropTypes.number,
    baseRateTimesOccurrenceAmount: PropTypes.number,
    advanceBookingMaxDays: PropTypes.number,
    advanceBookingMaxPeriod: PropTypes.number,
    pickupLocationOperationTimes: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
      }),
    ),
  }),
};

CarDetails.defaultProps = {
  itinerary: {},
  vendorMessages: {},
  getCarRateRulesStatus: 0,
  doShowMarkupRate: false,
  carRateRules: null,
};

export default memo(CarDetails);
